// @flow

import * as React from 'react';

import { useDispatch } from 'react-redux';

import { localeSelector, useTranslator } from '@gandi/translate';
import { DialogContent, ChoiceGroup, Button, DialogFooter } from '@gandi/ui';

import { useAppSelector } from 'src/hooks/useAppSelector';

import type { Phase } from '../../types';

import PhaseChoice from './PhaseChoice';
import { selectModalState, setModalState } from './module';

const isPhasePassed: (
  chosenPhaseName: string,
  phases: Array<Phase>,
) => boolean = (chosenPhaseName, phases) => {
  const now = new Date().getTime();
  const phase = phases.find(p => p.name === chosenPhaseName);

  if (!phase) return true;

  const end = phase?.ends_at ? new Date(phase.ends_at).getTime() : null;
  if (end === null) return false;

  return now > end;
};

const getRecommendedPhaseName: (
  string,
  string,
  Array<Phase>,
  void | number | boolean,
) => string = (fqdn, tld, phases, premium) => {
  const domain = fqdn?.split(`.${tld}`)[0];

  // T60637
  if (tld === 'new' && !isPhasePassed('landrush', phases)) return 'landrush';

  if ((domain?.length || 99) <= 3 && premium === 1)
    return (
      ['eap1', 'eap2', 'eap3'].find(
        current => !isPhasePassed(current, phases),
      ) || 'golive'
    );

  if (
    (domain?.length || 99) > 3 &&
    premium === 1 &&
    !isPhasePassed('eap4', phases)
  )
    return 'eap4';

  if ((domain?.length || 99) < 6 && !isPhasePassed('eap5', phases))
    return 'eap5';

  return 'golive';
};

type Props = {
  handleCancel: (evt: SyntheticMouseEvent<>) => void,
  handleConfirmation: (evt: SyntheticMouseEvent<>) => Promise<void>,
  setConfirmationStep: (value: boolean) => void,
};

export function PhaseSelectionStep({
  handleCancel,
  handleConfirmation,
  setConfirmationStep,
}: Props): React.MixedElement {
  const { __ } = useTranslator();
  const dispatch = useDispatch();

  const locale = useAppSelector(localeSelector);
  const userTimeZone =
    typeof Intl !== 'undefined'
      ? // eslint-disable-next-line
        Intl.DateTimeFormat().resolvedOptions().timeZone || ''
      : '';

  const {
    currency,
    fqdn,
    phase: modalPhase,
    phases,
    premium,
    prices,
    tld,
  } = useAppSelector(selectModalState);

  const recommendedPhase =
    fqdn &&
    tld &&
    phases &&
    getRecommendedPhaseName(fqdn, tld, phases, premium);
  const onlyChoice = phases?.length === 1 ? phases[0].name : null;

  const initialPhase = onlyChoice || recommendedPhase;
  const selectedPhase = modalPhase || initialPhase;

  const chooseConfirmationStep = React.useCallback(
    (chosenPhaseName: string): boolean => {
      const chosenPhase = phases.find(({ name }) => chosenPhaseName === name);
      const showConfirmationStep =
        !!chosenPhase &&
        !!premium &&
        !!chosenPhase.starts_at &&
        new Date(chosenPhase.starts_at).getTime() > new Date().getTime();
      setConfirmationStep(showConfirmationStep);
      return showConfirmationStep;
    },
    [phases, premium, setConfirmationStep],
  );

  function handlePhaseSelection(chosenPhaseName: string) {
    chooseConfirmationStep(chosenPhaseName);

    // set the phase to the modal
    // $FlowFixMe[not-an-array]
    dispatch(setModalState({ phase: chosenPhaseName }));
  }

  // Initialize the modal's phase selection with the recommended phase (initial value)
  React.useEffect(() => {
    if (!modalPhase) {
      // $FlowFixMe[not-an-array]
      dispatch(setModalState({ phase: initialPhase }));
    }
  }, [chooseConfirmationStep, dispatch, modalPhase, initialPhase]);

  async function handleSubmit(evt: SyntheticMouseEvent<>) {
    if (!chooseConfirmationStep(selectedPhase)) {
      handleConfirmation(evt);
    }
  }

  return (
    <>
      <DialogContent>
        <ChoiceGroup
          layout="column"
          value={selectedPhase}
          onValueChange={handlePhaseSelection}
        >
          {phases.map(currentPhase => (
            <PhaseChoice
              key={currentPhase.name}
              checked={currentPhase === selectedPhase}
              currency={currency}
              locale={locale}
              phase={currentPhase}
              price={prices?.create?.[currentPhase.name].price}
              recommended={recommendedPhase === currentPhase.name}
              tld={tld}
              userTimeZone={userTimeZone}
            />
          ))}
        </ChoiceGroup>
      </DialogContent>
      <DialogFooter>
        <Button type="button" kind="outline" onClick={handleCancel} small>
          {__('Cancel')}
        </Button>
        <Button onClick={handleSubmit} small>
          {__('Select')}
        </Button>
      </DialogFooter>
    </>
  );
}
